<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Utenti Influencer</h1>
        </div>

        <div class="search-container">
          <label class="search-label" for="influencerSearch">Ricerca rapida</label>
          <input
            id="influencerSearch"
            type="search"
            v-model.trim="searchQuery"
            @keyup.enter="refreshUsers"
            placeholder="Email, nome, cognome, studio o telefono"
            class="search-bar"
          />
          <button type="button" class="create-user-button create-user-button--neutral staff-button-neutral" @click="refreshUsers">
            <span class="material-icons">search</span>
            Aggiorna elenco
          </button>
          <button
            v-if="searchQuery"
            type="button"
            class="create-user-button create-user-button--neutral staff-button-neutral"
            @click="clearSearch"
          >
            <span class="material-icons">clear</span>
            Pulisci
          </button>
          <span v-if="isLoading" class="minimal-loader">Caricamento in corso...</span>
        </div>

        <section class="toolbar-summary" aria-live="polite">
          <span class="summary-pill">Utenti trovati: {{ filteredUsers.length }} di {{ users.length }}</span>
          <span v-if="searchQuery" class="summary-pill summary-pill--muted">Filtro: “{{ searchQuery }}”</span>
        </section>

        <div class="tickets">
          <table class="users-table">
            <thead>
              <tr>
                <th rowspan="2">Data di creazione</th>
                <th rowspan="2" class="email-column">Email</th>
                <th rowspan="2">Nome</th>
                <th rowspan="2">Cognome</th>
                <th rowspan="2">Studio</th>
                <th rowspan="2">Fonte</th>
                <th
                  v-for="program in trackingPrograms"
                  :key="program.index"
                  :colspan="program.steps.length"
                  :class="['program-group-column', `program-group-column--${program.index}`]"
                >
                  {{ program.label }}
                </th>
                <th rowspan="2"></th>
              </tr>
              <tr>
                <th
                  v-for="column in trackingColumns"
                  :key="column.id"
                  :class="[
                    'step-status-column',
                    `step-status-column--program-${column.programIndex}`,
                    {
                      'step-status-column--group-start': column.isFirstInProgram,
                      'step-status-column--group-end': column.isLastInProgram,
                    },
                  ]"
                >
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in filteredUsers" :key="user.uid">
                <td>{{ formatDate(user.created_time) }}</td>
                <td class="email-cell">
                  <div class="email-text" :title="user.email">{{ user.email }}</div>
                </td>
                <td>{{ user.name || '—' }}</td>
                <td>{{ user.surname || '—' }}</td>
                <td>{{ user.studio || '—' }}</td>
                <td>
                  <span
                    :class="['source-chip', sourceStatusClass(user)]"
                    :title="sourceStatusTitle(user)"
                  >
                    {{ user.influencerSource }}
                  </span>
                </td>
                <td
                  v-for="column in trackingColumns"
                  :key="column.id"
                  :class="[
                    'step-status-cell',
                    `step-status-cell--program-${column.programIndex}`,
                    {
                      'step-status-cell--group-start': column.isFirstInProgram,
                      'step-status-cell--group-end': column.isLastInProgram,
                    },
                  ]"
                >
                  <div class="step-status-stack" :title="stepTrackingTitle(user, column)">
                    <span class="step-status-pill" :class="stepTrackingClass(user, column)">
                      <span>{{ stepTrackingLabel(user, column) }}</span>
                      <span
                        v-if="stepTrackingShowsApprovedIcon(user, column)"
                        class="step-status-icon material-icons"
                        title="Questionario approvato e inviato"
                        aria-label="Questionario approvato e inviato"
                      >
                        mark_email_read
                      </span>
                    </span>
                    <small
                      v-if="stepTrackingMeta(user, column)"
                      class="step-status-meta"
                    >
                      {{ stepTrackingMeta(user, column) }}
                    </small>
                  </div>
                </td>
                <td class="actions-cell">
                  <div class="buttons-container">
                    <button @click="viewUserDetails(user.uid)" title="Apri dettaglio utente">
                      <span class="material-icons">visibility</span>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!isLoading && filteredUsers.length === 0">
                <td :colspan="emptyStateColspan" class="empty-state">Nessun utente Influencer trovato.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import SidebarComponent from '../SidebarComponent.vue';

const INFLUENCER_SOURCE = 'Influencer';
const LEGACY_VIP_SOURCE = 'VIP (legacy)';
const PROGRAMMI_CHUNK_SIZE = 10;
const PROTOCOLLO_TOP_LABEL = 'Protocollo 3 top';
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const TRACKING_PROGRAMS = [
  {
    index: 1,
    label: 'Programma 1',
    steps: [
      { slot: 'questionario', label: 'Questionario' },
      { slot: 'consulenza_nutrizionale', label: 'Cons. nutriz.' },
      { slot: 'consegna', label: 'Consegna' },
      { slot: 'consulenza_tecnica', label: 'Cons. tecnica' },
      { slot: 'check_finale', label: 'Check finale' },
    ],
  },
  {
    index: 2,
    label: 'Programma 2',
    steps: [
      { slot: 'consegna', label: 'Consegna' },
      { slot: 'consulenza_tecnica', label: 'Cons. tecnica' },
      { slot: 'check_finale', label: 'Check finale' },
    ],
  },
  {
    index: 3,
    label: 'Programma 3',
    steps: [
      { slot: 'consegna', label: 'Consegna' },
      { slot: 'consulenza_tecnica', label: 'Cons. tecnica' },
      { slot: 'check_finale', label: 'Check finale' },
    ],
  },
];
const TRACKING_COLUMNS = TRACKING_PROGRAMS.flatMap((program) =>
  program.steps.map((step, stepIndex) => ({
    id: `p${program.index}_${step.slot}`,
    programIndex: program.index,
    programLabel: program.label,
    stepSlot: step.slot,
    label: step.label,
    isFirstInProgram: stepIndex === 0,
    isLastInProgram: stepIndex === program.steps.length - 1,
  }))
);
const EMPTY_STATE_COLSPAN = 6 + TRACKING_COLUMNS.length + 1;

const toSearchText = (value) => String(value || '').toLowerCase().trim();
const normalizeProgramKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
const compactProgramKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const chunkList = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toComparableString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch {
      return '';
    }
  }
  return String(value);
};

const formatDateLabel = (value) => {
  const date = toDateSafe(value);
  return date ? date.toLocaleDateString('it-IT') : '';
};

const dayStartMs = (value) => {
  const date = toDateSafe(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const getDayDiffFromToday = (value) => {
  const target = dayStartMs(value);
  const today = dayStartMs(new Date());
  if (target == null || today == null) return null;
  return Math.round((target - today) / DAY_IN_MS);
};

const formatDayDiffLabel = (diff) => {
  if (diff == null) return '';
  if (diff === 0) return 'oggi';
  if (diff < 0) {
    const abs = Math.abs(diff);
    return abs === 1 ? 'ritardo 1 gg' : `ritardo ${abs} gg`;
  }
  return diff === 1 ? 'tra 1 gg' : `tra ${diff} gg`;
};

const hasTruthValue = (value) => {
  return value === true || value === 'true' || value === 1 || value === '1';
};

const resolveInfluencerSource = (user = {}) => {
  if (hasTruthValue(user.influencer)) {
    return INFLUENCER_SOURCE;
  }
  if (hasTruthValue(user.vip)) {
    return LEGACY_VIP_SOURCE;
  }
  return INFLUENCER_SOURCE;
};

const getCreatedTime = (value) => {
  if (!value) {
    return 0;
  }

  if (typeof value?.toDate === 'function') {
    const parsed = value.toDate();
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const resolveOrigin = (user) => {
  if (hasTruthValue(user.influencer)) {
    return INFLUENCER_SOURCE;
  }
  if (hasTruthValue(user.vip)) {
    return LEGACY_VIP_SOURCE;
  }
  return INFLUENCER_SOURCE;
};

const mergeById = (incoming) => {
  const merged = new Map();
  incoming.forEach((entry) => {
    const previous = merged.get(entry.uid);
    if (!previous) {
      merged.set(entry.uid, { ...entry });
      return;
    }
    merged.set(entry.uid, {
      ...previous,
      ...entry,
      influencerSource:
        resolveInfluencerSource({ ...previous, ...entry }) || entry.influencerSource || previous.influencerSource,
    });
  });
  return Array.from(merged.values());
};

const normalizeStepsArray = (steps) => {
  if (Array.isArray(steps)) {
    return steps.map((step) => (step && typeof step === 'object' ? { ...step } : step));
  }

  if (steps && typeof steps === 'object') {
    return Object.keys(steps)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => {
        const step = steps[key];
        return step && typeof step === 'object' ? { ...step } : step;
      });
  }

  return [];
};

const normalizePercorsoRecord = (percorso = {}) => ({
  ...percorso,
  steps: normalizeStepsArray(percorso?.steps),
});

const parseSequenceIndex = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const match = value.match(/\d+/);
    return match ? Number(match[0]) : null;
  }
  return null;
};

const stepKeyFromName = (name = '') => {
  const normalized = String(name || '').toLowerCase();
  if (normalized.includes('check') || normalized.includes('scadenza')) return 'check_finale';
  if (normalized.includes('questionario')) return 'questionario';
  if (normalized.includes('consegna')) return 'consegna';
  if (normalized.includes('consulenza')) return 'consulenza';
  return '';
};

const normalizeConsulenzaStatus = (status) => String(status || '').trim().toLowerCase();

const isConsulenzaStatusDone = (status) => {
  const normalized = normalizeConsulenzaStatus(status);
  return normalized === 'fatta' || normalized === 'rifiutata' || normalized === 'scalata';
};

const isBlockingStepForSequence = (step) => {
  const name = String(step?.name || '').toLowerCase();
  if (!name) return true;
  if (name.includes('feedback')) return false;
  return true;
};

const isStepEffectivelyDone = (step) => {
  if (!step) return false;
  const name = String(step?.name || '').toLowerCase();
  if (name.includes('consulenza') && isConsulenzaStatusDone(step?.consulenzaStatus)) {
    return true;
  }
  return !!step?.done;
};

const isRealCheckFinaleStep = (step = {}) => {
  const name = String(step?.name || '').toLowerCase();
  const isCheck = name.includes('check') || name.includes('scadenza');
  if (!isCheck) return false;
  return !step?.convertedFromQuestionario;
};

const blockingStepsOnly = (steps = []) =>
  (Array.isArray(steps) ? steps : []).filter(isBlockingStepForSequence);

const hasCheckFinaleDone = (steps = []) =>
  (Array.isArray(steps) ? steps : []).some(
    (step) => isRealCheckFinaleStep(step) && isStepEffectivelyDone(step)
  );

const isPercorsoFullyCompleted = (percorso) => {
  const steps = Array.isArray(percorso?.steps) ? percorso.steps : [];
  if (!steps.length) return false;
  if (hasCheckFinaleDone(steps)) return true;
  const blockingSteps = blockingStepsOnly(steps);
  if (!blockingSteps.length) return true;
  return blockingSteps.every(isStepEffectivelyDone);
};

const isPercorsoHistoryLocked = (percorso) => !!percorso?.historyLocked;
const hasSequenceUnlockOverride = (percorso) => percorso?.sequenceUnlockOverride === true;
const hasCompletedUnlockOverride = (percorso) => percorso?.completedUnlockOverride === true;
const isPercorsoCompleteForLocking = (percorso) =>
  isPercorsoFullyCompleted(percorso) || isPercorsoHistoryLocked(percorso);

const sortPercorsiBySequence = (a, b) => {
  const ix = Number(a?.sequenceIndex) || 0;
  const iy = Number(b?.sequenceIndex) || 0;
  if (ix !== iy) return ix - iy;
  const ax = toComparableString(a?.plannedStartAt || a?.createdAt);
  const ay = toComparableString(b?.plannedStartAt || b?.createdAt);
  return ax.localeCompare(ay);
};

const buildOrderedPercorsoGroups = (rows = []) => {
  const normalizedRows = rows
    .map((row) => normalizePercorsoRecord(row))
    .filter((row) => !row?.consulenzeOnly);

  const groups = new Map();
  const groupMeta = new Map();

  for (const percorso of normalizedRows) {
    const groupId = percorso.sequenceGroupId || percorso.id;
    const groupRows = groups.get(groupId) || [];
    groupRows.push(percorso);
    groups.set(groupId, groupRows);

    const anchorThis = toComparableString(percorso.plannedStartAt || percorso.createdAt);
    const meta = groupMeta.get(groupId) || { anchor: anchorThis || '' };
    if (!meta.anchor || (anchorThis && anchorThis < meta.anchor)) {
      meta.anchor = anchorThis;
    }
    groupMeta.set(groupId, meta);
  }

  const groupOrder = Array.from(groups.keys()).sort((a, b) => {
    const aa = toComparableString(groupMeta.get(a)?.anchor);
    const bb = toComparableString(groupMeta.get(b)?.anchor);
    return bb.localeCompare(aa);
  });

  return groupOrder.map((groupId) => ({
    id: groupId,
    anchor: groupMeta.get(groupId)?.anchor || '',
    percorsi: [...(groups.get(groupId) || [])].sort(sortPercorsiBySequence),
  }));
};

const buildSequentialLocks = (groups = []) => {
  const locks = {};

  for (const group of groups) {
    const sorted = [...(group?.percorsi || [])].sort(sortPercorsiBySequence);
    if (!sorted.length) continue;

    const gatingActive = sorted.length > 1 || Number(sorted[0]?.sequenceTotal) > 1;
    let blockNext = false;

    sorted.forEach((percorso) => {
      if (!gatingActive) {
        locks[percorso.id] = false;
        return;
      }

      const override = hasSequenceUnlockOverride(percorso);
      locks[percorso.id] = blockNext && !override;
      const isComplete = isPercorsoCompleteForLocking(percorso);
      blockNext = !isComplete;
    });
  }

  return locks;
};

const buildPreviousGroupLocks = (groups = []) => {
  const locks = {};
  let blockFollowing = false;
  const orderedGroups = [...groups].sort((a, b) =>
    String(a?.anchor || '').localeCompare(String(b?.anchor || ''))
  );

  for (const group of orderedGroups) {
    const list = group?.percorsi || [];
    const groupCompleted = list.every(
      (percorso) => hasSequenceUnlockOverride(percorso) || isPercorsoCompleteForLocking(percorso)
    );

    list.forEach((percorso) => {
      locks[percorso.id] = blockFollowing && !hasSequenceUnlockOverride(percorso);
    });

    if (!groupCompleted) {
      blockFollowing = true;
    }
  }

  return locks;
};

const getPercorsoLockReason = (percorso, sequentialLocks = {}, previousGroupLocks = {}) => {
  if (isPercorsoHistoryLocked(percorso)) return 'history';
  if (isPercorsoFullyCompleted(percorso) && !hasCompletedUnlockOverride(percorso)) return 'completed';
  if (previousGroupLocks[percorso?.id]) return 'previous_group';
  if (sequentialLocks[percorso?.id]) return 'sequence';
  return null;
};

const normalizeConsulenzaType = (raw) => {
  const value = String(raw || '').toLowerCase();
  if (value.includes('nutriz')) return 'nutrizionale';
  if (value.includes('tecnic')) return 'tecnica';
  return '';
};

const isFirstProgramInSequence = (percorso) => {
  const idx = parseSequenceIndex(percorso?.sequenceIndex);
  const total = Number(percorso?.sequenceTotal);
  if (idx === 1) return true;
  if (idx == null && (!Number.isFinite(total) || total <= 1)) return true;
  return false;
};

const STEP_ORDER = {
  questionario: 3,
  consegna: 6,
  consulenza: 10,
  check_finale: 12,
};

const stepOrderForPercorso = (percorso, key, consulenzaType, step) => {
  const normalizedType = normalizeConsulenzaType(consulenzaType);
  if (isFirstProgramInSequence(percorso)) {
    if (key === 'questionario') return 1;
    if (key === 'check_finale' && step?.convertedFromQuestionario) return 1;
    if (key === 'consulenza' && normalizedType === 'nutrizionale') return 2;
    if (key === 'consegna') return 3;
    if (key === 'consulenza' && normalizedType === 'tecnica') return 4;
    if (key === 'check_finale') return 5;
  }
  let order = STEP_ORDER[key] ?? 99;
  if (key === 'consulenza' && normalizedType === 'nutrizionale') {
    order = 2;
  }
  return order;
};

const buildStepRowsForPercorso = (percorso) => {
  const all = Array.isArray(percorso?.steps) ? percorso.steps : [];
  if (!all.length) return [];

  const rows = all
    .map((step, idx) => {
      const key = stepKeyFromName(step?.name);
      if (!key) return null;
      return {
        key,
        realIndex: idx,
        step,
        isDone: isStepEffectivelyDone(step),
        order: stepOrderForPercorso(percorso, key, step?.consulenzaType, step),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.realIndex - b.realIndex;
    });

  const consegnaRow = rows.find((row) => row.key === 'consegna');
  const consegnaDone = consegnaRow ? consegnaRow.isDone : true;

  rows.forEach((row) => {
    if (row.key !== 'check_finale') return;
    const checkAlreadyDone = row.isDone;
    const hasOverride = !!row.step?.allowCheckOverride;
    const converted = !!row.step?.convertedFromQuestionario;
    row.isLockedByConsegna = !converted && !checkAlreadyDone && !consegnaDone && !hasOverride;
  });

  return rows;
};

const matchesProtocolloTop = (percorso = {}) => {
  const candidates = [percorso?.title, percorso?.productName];
  return candidates.some((candidate) => {
    const normalized = normalizeProgramKey(candidate);
    const compact = compactProgramKey(candidate);
    return normalized.includes('protocollo 3 top') || compact.includes('protocollo3top');
  });
};

const normalizeAdminDailyState = (rawState, sentFlag = false) => {
  const state = String(rawState || '').trim().toLowerCase();
  if (state === 'inviato' || state === 'nascosto') return state;
  return sentFlag ? 'inviato' : '';
};

const resolveStepAdminDailyState = (step) =>
  normalizeAdminDailyState(step?.adminDailyState, step?.adminDailySent);

const resolveProgramLabel = (percorso) =>
  String(percorso?.title || percorso?.productName || PROTOCOLLO_TOP_LABEL).trim() || PROTOCOLLO_TOP_LABEL;

const resolveTrackingRowForSlot = (rows = [], slot = '') => {
  if (slot === 'questionario') {
    return (
      rows.find((row) => row.key === 'questionario') ||
      rows.find((row) => row.key === 'check_finale' && row.step?.convertedFromQuestionario) ||
      null
    );
  }
  if (slot === 'consulenza_nutrizionale') {
    return (
      rows.find(
        (row) => row.key === 'consulenza' && normalizeConsulenzaType(row.step?.consulenzaType) === 'nutrizionale'
      ) || null
    );
  }
  if (slot === 'consulenza_tecnica') {
    return (
      rows.find(
        (row) => row.key === 'consulenza' && normalizeConsulenzaType(row.step?.consulenzaType) === 'tecnica'
      ) || null
    );
  }
  if (slot === 'check_finale') {
    return rows.find((row) => row.key === 'check_finale' && !row.step?.convertedFromQuestionario) || null;
  }
  if (slot === 'consegna') {
    return rows.find((row) => row.key === 'consegna') || null;
  }
  return null;
};

const buildTrackingStepCell = (base, status, metaOverride = '') => {
  const diff = getDayDiffFromToday(base.plannedAt);
  let label = '—';
  let meta = metaOverride || '';

  if (status === 'complete') {
    label = 'Fatto';
    meta = base.doneAt ? formatDateLabel(base.doneAt) : meta;
  } else if (status === 'overdue') {
    label = 'Ritardo';
    meta = meta || formatDayDiffLabel(diff);
  } else if (status === 'today') {
    label = 'Oggi';
    meta = meta || formatDateLabel(base.plannedAt);
  } else if (status === 'on_track') {
    label = 'In linea';
    meta = meta || formatDayDiffLabel(diff);
  } else if (status === 'todo') {
    label = 'Da fare';
    meta = meta || 'senza data';
  } else if (status === 'waiting') {
    label = 'Attesa';
  } else if (status === 'blocked') {
    label = 'Fermo';
  }

  const title = [
    base.programLabel,
    base.stepLabel,
    `Stato: ${label}`,
    base.doneAt ? `Completato il ${formatDateLabel(base.doneAt)}` : '',
    base.plannedAt && status !== 'complete' ? `Previsto il ${formatDateLabel(base.plannedAt)}` : '',
    meta,
  ]
    .filter(Boolean)
    .join(' • ');

  return {
    ...base,
    status,
    label,
    meta,
    title,
  };
};

const buildMissingProgramTracking = (programDef, previousProgram = null) => {
  const blockedByPrevious = !!previousProgram && !previousProgram.complete;
  const status = blockedByPrevious ? 'blocked' : 'missing';
  const meta = blockedByPrevious ? 'prog. precedente' : '';
  const steps = {};

  programDef.steps.forEach((stepDef) => {
    steps[stepDef.slot] = buildTrackingStepCell(
      {
        programLabel: `${programDef.label} • ${PROTOCOLLO_TOP_LABEL}`,
        stepLabel: stepDef.label,
        available: false,
        done: false,
        doneAt: null,
        plannedAt: null,
        adminApproved: false,
        dailyState: '',
      },
      status,
      meta
    );
  });

  return {
    exists: false,
    complete: false,
    programIndex: programDef.index,
    programLabel: `${programDef.label} • ${PROTOCOLLO_TOP_LABEL}`,
    lockReason: blockedByPrevious ? 'previous_group' : 'missing',
    steps,
  };
};

const buildProgramTracking = (percorso, programDef, lockReason = null, previousProgram = null) => {
  if (!percorso) {
    return buildMissingProgramTracking(programDef, previousProgram);
  }

  const rows = buildStepRowsForPercorso(percorso);
  const programLabel = resolveProgramLabel(percorso);
  const blockedByPreviousProgram = lockReason === 'sequence' || lockReason === 'previous_group';
  const rawSteps = programDef.steps.map((stepDef) => {
    const row = resolveTrackingRowForSlot(rows, stepDef.slot);
    return {
      slot: stepDef.slot,
      stepLabel: stepDef.label,
      programLabel,
      available: !!row,
      done: !!row?.isDone,
      doneAt: row?.step?.doneAt || (row?.isDone ? row?.step?.plannedAt || null : null),
      plannedAt: row?.step?.plannedAt || null,
      adminApproved: row?.step?.adminStatus === 'approved',
      dailyState: resolveStepAdminDailyState(row?.step),
      isLockedByConsegna: !!row?.isLockedByConsegna,
    };
  });

  const firstPendingIndex = rawSteps.findIndex((step) => step.available && !step.done);
  const steps = {};

  rawSteps.forEach((step, index) => {
    let status = 'missing';
    let meta = '';

    if (!step.available) {
      status = 'missing';
    } else if (step.done) {
      status = 'complete';
    } else if (blockedByPreviousProgram) {
      status = 'blocked';
      meta = 'prog. precedente';
    } else if (step.isLockedByConsegna) {
      status = 'waiting';
      meta = 'attende consegna';
    } else if (firstPendingIndex !== -1 && index > firstPendingIndex) {
      status = 'waiting';
      meta = 'step precedente';
    } else {
      const diff = getDayDiffFromToday(step.plannedAt);
      if (diff == null) status = 'todo';
      else if (diff < 0) status = 'overdue';
      else if (diff === 0) status = 'today';
      else status = 'on_track';
    }

    steps[step.slot] = buildTrackingStepCell(step, status, meta);
  });

  return {
    exists: true,
    complete: isPercorsoCompleteForLocking(percorso),
    programIndex: programDef.index,
    programLabel,
    lockReason,
    steps,
  };
};

const buildEmptyProtocolloTracking = () => ({
  hasProtocollo: false,
  groupLabel: PROTOCOLLO_TOP_LABEL,
  programs: TRACKING_PROGRAMS.reduce((acc, programDef) => {
    acc[programDef.index] = buildMissingProgramTracking(programDef, null);
    return acc;
  }, {}),
});

const buildProtocolloTrackingSummary = (rows = []) => {
  const groups = buildOrderedPercorsoGroups(rows);
  if (!groups.length) return buildEmptyProtocolloTracking();

  const sequentialLocks = buildSequentialLocks(groups);
  const previousGroupLocks = buildPreviousGroupLocks(groups);
  const protocolloGroups = groups
    .map((group) => ({
      ...group,
      percorsi: (group?.percorsi || []).filter((percorso) => matchesProtocolloTop(percorso)),
    }))
    .filter((group) => group.percorsi.length);

  if (!protocolloGroups.length) return buildEmptyProtocolloTracking();

  const selectedGroup =
    protocolloGroups.find((group) =>
      group.percorsi.some((percorso) => {
        const lockReason = getPercorsoLockReason(percorso, sequentialLocks, previousGroupLocks);
        return !lockReason && !isPercorsoFullyCompleted(percorso);
      })
    ) || protocolloGroups[0];

  const percorsiByIndex = new Map();
  [...(selectedGroup?.percorsi || [])]
    .sort(sortPercorsiBySequence)
    .forEach((percorso, idx) => {
      const parsedIndex = parseSequenceIndex(percorso?.sequenceIndex);
      const programIndex = parsedIndex || idx + 1;
      if (!percorsiByIndex.has(programIndex)) {
        percorsiByIndex.set(programIndex, percorso);
      }
    });

  const programs = {};
  let previousProgram = null;

  TRACKING_PROGRAMS.forEach((programDef) => {
    const percorso = percorsiByIndex.get(programDef.index) || null;
    const lockReason = percorso
      ? getPercorsoLockReason(percorso, sequentialLocks, previousGroupLocks)
      : null;
    const summary = buildProgramTracking(percorso, programDef, lockReason, previousProgram);
    programs[programDef.index] = summary;
    previousProgram = summary;
  });

  return {
    hasProtocollo: true,
    groupLabel: resolveProgramLabel(selectedGroup?.percorsi?.[0]),
    programs,
  };
};

export default {
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const users = ref([]);
    const searchQuery = ref('');
    const isLoading = ref(false);
    const router = useRouter();

    const loadProtocolloTrackingByUserIds = async (userIds = []) => {
      const uniqueIds = Array.from(
        new Set(userIds.map((value) => String(value || '').trim()).filter(Boolean))
      );
      const trackingByUid = new Map();
      if (!uniqueIds.length) return trackingByUid;

      const percorsiByUid = new Map();
      uniqueIds.forEach((uid) => percorsiByUid.set(uid, []));

      for (const chunk of chunkList(uniqueIds, PROGRAMMI_CHUNK_SIZE)) {
        const [uidSnap, userUidSnap] = await Promise.all([
          getDocs(query(collection(db, 'percorsi'), where('uid', 'in', chunk))),
          getDocs(query(collection(db, 'percorsi'), where('userUid', 'in', chunk))),
        ]);

        const seenPercorsoIds = new Set();
        [uidSnap, userUidSnap].forEach((snapshot) => {
          snapshot.forEach((document) => {
            if (seenPercorsoIds.has(document.id)) return;
            seenPercorsoIds.add(document.id);

            const data = document.data() || {};
            const uid = String(data.uid || data.userUid || '').trim();
            if (!uid) return;
            const rows = percorsiByUid.get(uid) || [];
            rows.push({ id: document.id, ...data });
            percorsiByUid.set(uid, rows);
          });
        });
      }

      uniqueIds.forEach((uid) => {
        trackingByUid.set(uid, buildProtocolloTrackingSummary(percorsiByUid.get(uid) || []));
      });

      return trackingByUid;
    };

    const buildInfluencerRows = async () => {
      const usersCollection = collection(db, 'users');
      const rows = [];
      const influencerQueryPlans = [
        { field: 'influencer', value: true, source: INFLUENCER_SOURCE },
        { field: 'influencer', value: 'true', source: INFLUENCER_SOURCE },
        { field: 'influencer', value: 1, source: INFLUENCER_SOURCE },
        { field: 'vip', value: true, source: LEGACY_VIP_SOURCE },
        { field: 'vip', value: 'true', source: LEGACY_VIP_SOURCE },
        { field: 'vip', value: 1, source: LEGACY_VIP_SOURCE },
      ];

      const queryRows = await Promise.all(
        influencerQueryPlans.map(async ({ field, value }) => {
          const snapshot = await getDocs(query(usersCollection, where(field, '==', value)));
          return snapshot.docs.map((document) => {
            const data = document.data() || {};
            return {
              uid: document.id,
              ...data,
              influencerSource: resolveInfluencerSource(data) || INFLUENCER_SOURCE,
            };
          });
        })
      );

      queryRows.forEach((items) => rows.push(...items));
      return mergeById(rows);
    };

    const fetchInfluencerUsers = async () => {
      isLoading.value = true;
      try {
        const rows = await buildInfluencerRows();
        const trackingByUid = await loadProtocolloTrackingByUserIds(rows.map((user) => user.uid));
        users.value = rows
          .map((user) => ({
            ...user,
            influencerSource: user.influencerSource || resolveOrigin(user),
            _protocolloTracking: trackingByUid.get(user.uid) || buildEmptyProtocolloTracking(),
          }))
          .sort((a, b) => getCreatedTime(b.created_time) - getCreatedTime(a.created_time));
      } catch (error) {
        console.error('Errore nel caricamento utenti Influencer:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const formatDate = (timestamp) => {
      if (timestamp && typeof timestamp.toDate === 'function') {
        const date = timestamp.toDate();
        return date.toLocaleDateString('it-IT');
      }

      if (timestamp) {
        const parsed = new Date(timestamp);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleDateString('it-IT');
        }
      }

      return '—';
    };

    const openInNewTab = (route) => {
      const resolved = router.resolve(route);
      window.open(resolved.href, '_blank', 'noopener');
    };

    const viewUserDetails = (userId) => {
      openInNewTab({ name: 'UserDetails', params: { userId } });
    };

    const resolveProtocolloTracking = (user) => user?._protocolloTracking || null;

    const resolveTrackingCell = (user, column) => {
      const tracking = resolveProtocolloTracking(user);
      return tracking?.programs?.[column?.programIndex]?.steps?.[column?.stepSlot] || null;
    };

    const resolvePrimaryTrackingCell = (user) => {
      const cells = TRACKING_COLUMNS.map((column) => resolveTrackingCell(user, column)).filter(Boolean);
      const availableCells = cells.filter((cell) => cell.available);
      if (!availableCells.length) return null;
      return availableCells.find((cell) => cell.status !== 'complete') || null;
    };

    const stepTrackingLabel = (user, column) => resolveTrackingCell(user, column)?.label || '—';

    const stepTrackingMeta = (user, column) => resolveTrackingCell(user, column)?.meta || '';

    const stepTrackingTitle = (user, column) =>
      resolveTrackingCell(user, column)?.title || `${column?.programLabel || 'Programma'} • ${column?.label || 'Step'}`;

    const stepTrackingClass = (user, column) => {
      const status = resolveTrackingCell(user, column)?.status || 'missing';
      if (status === 'complete') return 'step-status-pill--done';
      if (status === 'overdue') return 'step-status-pill--danger';
      if (status === 'today') return 'step-status-pill--warning';
      if (status === 'on_track') return 'step-status-pill--info';
      if (status === 'todo') return 'step-status-pill--neutral';
      if (status === 'waiting') return 'step-status-pill--waiting';
      if (status === 'blocked') return 'step-status-pill--blocked';
      return 'step-status-pill--empty';
    };

    const stepTrackingShowsApprovedIcon = (user, column) => {
      if (column?.stepSlot !== 'questionario') return false;
      const cell = resolveTrackingCell(user, column);
      if (!cell?.available) return false;
      return cell.adminApproved && cell.dailyState === 'inviato';
    };

    const sourceStatusClass = (user) => {
      const primaryCell = resolvePrimaryTrackingCell(user);
      if (!primaryCell) return 'source-chip--neutral';
      return primaryCell.status === 'overdue' ? 'source-chip--delayed' : 'source-chip--on-track';
    };

    const sourceStatusTitle = (user) => {
      const tracking = resolveProtocolloTracking(user);
      const primaryCell = resolvePrimaryTrackingCell(user);
      if (!tracking?.hasProtocollo) return 'Nessun Protocollo 3 top trovato.';
      if (!primaryCell) return 'Percorso in linea: tutti gli step disponibili risultano completati.';
      if (primaryCell.status === 'overdue') {
        return `Percorso in ritardo su ${primaryCell.stepLabel}${primaryCell.meta ? ` • ${primaryCell.meta}` : ''}`;
      }
      return `Percorso in linea su ${primaryCell.stepLabel}${primaryCell.meta ? ` • ${primaryCell.meta}` : ''}`;
    };

    const filteredUsers = computed(() => {
      if (!searchQuery.value) {
        return users.value;
      }

      const tokens = toSearchText(searchQuery.value)
        .split(/\s+/)
        .filter(Boolean);

      return users.value.filter((user) => {
        const haystack = [
          user.email,
          user.name,
          user.surname,
          user.studio,
          user.phone,
          user.influencerSource,
        ]
          .map(toSearchText)
          .join(' ');

        return tokens.every((token) => haystack.includes(token));
      });
    });

    const clearSearch = () => {
      searchQuery.value = '';
    };

    const refreshUsers = () => {
      fetchInfluencerUsers();
    };

    onMounted(fetchInfluencerUsers);

    return {
      users,
      searchQuery,
      isLoading,
      filteredUsers,
      refreshUsers,
      clearSearch,
      formatDate,
      viewUserDetails,
      trackingPrograms: TRACKING_PROGRAMS,
      trackingColumns: TRACKING_COLUMNS,
      emptyStateColspan: EMPTY_STATE_COLSPAN,
      stepTrackingLabel,
      stepTrackingMeta,
      stepTrackingTitle,
      stepTrackingClass,
      stepTrackingShowsApprovedIcon,
      sourceStatusClass,
      sourceStatusTitle,
    };
  },
};
</script>

<style scoped>
.search-container {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin: 18px 0 12px;
  padding: 20px 16px 14px;
  border-radius: 18px;
  border: 1px solid #dbe2f0;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
}

.search-label {
  position: absolute;
  top: -11px;
  left: 14px;
  background: #f8fafc;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 0.76rem;
  color: #475569;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.search-bar {
  flex: 1 1 280px;
  min-width: 240px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  padding: 10px 14px;
  font-size: 0.95rem;
  color: #0f172a;
}

.search-bar:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.65);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.toolbar-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: -6px 0 18px;
}

.summary-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #e0e7ff;
  color: #1e3a8a;
  font-size: 0.82rem;
  padding: 8px 12px;
  border: 1px solid rgba(99, 102, 241, 0.22);
  font-weight: 600;
  letter-spacing: 0.02em;
}

.summary-pill--muted {
  background: #f1f5f9;
  color: #334155;
  border-color: rgba(148, 163, 184, 0.45);
}

.create-user-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 999px;
  border: none;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(120deg, #22c55e, #16a34a);
  white-space: nowrap;
}

.create-user-button--neutral {
  background: linear-gradient(120deg, #64748b, #475569);
}

.minimal-loader {
  font-size: 0.9rem;
  color: #2563eb;
}

.tickets {
  border-radius: 18px;
  overflow-x: auto;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06);
}

.users-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.95rem;
  color: #0f172a;
  min-width: 2280px;
}

.users-table thead th {
  background: linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%);
  padding: 14px 18px;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  border-bottom: 1px solid #dbe2f0;
  color: #0f172a;
  text-align: center;
}

.users-table thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 3;
}

.users-table thead tr:nth-child(2) th {
  position: sticky;
  top: 46px;
  z-index: 2;
  padding-top: 10px;
  padding-bottom: 10px;
  font-size: 0.68rem;
}

.program-group-column {
  background: linear-gradient(180deg, #e8eefb 0%, #dde7f8 100%) !important;
  color: #1e3a8a !important;
  box-shadow: inset 0 -1px 0 rgba(148, 163, 184, 0.35);
}

.program-group-column--1 {
  background: linear-gradient(180deg, #e8eefb 0%, #dbeafe 100%) !important;
}

.program-group-column--2 {
  background: linear-gradient(180deg, #fff4db 0%, #fef3c7 100%) !important;
  color: #92400e !important;
  box-shadow: inset 3px 0 0 rgba(245, 158, 11, 0.35), inset 0 -1px 0 rgba(148, 163, 184, 0.35);
}

.program-group-column--3 {
  background: linear-gradient(180deg, #e7f8ef 0%, #dcfce7 100%) !important;
  color: #166534 !important;
  box-shadow: inset 3px 0 0 rgba(34, 197, 94, 0.28), inset 0 -1px 0 rgba(148, 163, 184, 0.35);
}

.users-table tbody tr {
  transition: background-color 0.2s ease;
}

.users-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.users-table tbody tr:hover {
  background: #eef6ff;
}

.users-table tbody td {
  padding: 14px 18px;
  border-bottom: 1px solid #e4e9f2;
  vertical-align: middle;
  color: #0f172a;
}

.email-cell {
  width: 30%;
}

.email-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-chip {
  display: inline-flex;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.source-chip--on-track {
  background: #dcfce7;
  color: #166534;
}

.source-chip--delayed {
  background: #ffedd5;
  color: #c2410c;
}

.source-chip--neutral {
  background: #e2e8f0;
  color: #475569;
}

.step-status-column,
.step-status-cell {
  text-align: center;
}

.step-status-column--program-1,
.step-status-cell--program-1 {
  background: rgba(219, 234, 254, 0.18);
}

.step-status-column--program-2,
.step-status-cell--program-2 {
  background: rgba(254, 243, 199, 0.3);
}

.step-status-column--program-3,
.step-status-cell--program-3 {
  background: rgba(220, 252, 231, 0.24);
}

.step-status-column--group-start,
.step-status-cell--group-start {
  border-left: 3px solid rgba(148, 163, 184, 0.4);
}

.step-status-column--program-2.step-status-column--group-start,
.step-status-cell--program-2.step-status-cell--group-start {
  border-left-color: rgba(245, 158, 11, 0.45);
}

.step-status-column--program-3.step-status-column--group-start,
.step-status-cell--program-3.step-status-cell--group-start {
  border-left-color: rgba(34, 197, 94, 0.38);
}

.step-status-column--group-end,
.step-status-cell--group-end {
  box-shadow: inset -1px 0 0 rgba(148, 163, 184, 0.18);
}

.step-status-stack {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 88px;
}

.step-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 30px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: #f8fafc;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1.1;
  white-space: nowrap;
}

.step-status-icon {
  font-size: 0.95rem;
  line-height: 1;
}

.step-status-meta {
  font-size: 0.69rem;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
}

.step-status-pill--done {
  border-color: rgba(34, 197, 94, 0.26);
  background: #ecfdf5;
  color: #15803d;
}

.step-status-pill--info {
  border-color: rgba(59, 130, 246, 0.26);
  background: #eff6ff;
  color: #1d4ed8;
}

.step-status-pill--warning {
  border-color: rgba(245, 158, 11, 0.26);
  background: #fff7ed;
  color: #b45309;
}

.step-status-pill--danger {
  border-color: rgba(239, 68, 68, 0.28);
  background: #fef2f2;
  color: #b91c1c;
}

.step-status-pill--neutral {
  border-color: rgba(148, 163, 184, 0.28);
  background: #f8fafc;
  color: #475569;
}

.step-status-pill--waiting {
  border-color: rgba(148, 163, 184, 0.32);
  background: #f1f5f9;
  color: #475569;
}

.step-status-pill--blocked {
  border-color: rgba(100, 116, 139, 0.35);
  background: #e2e8f0;
  color: #334155;
}

.step-status-pill--empty {
  border-color: rgba(148, 163, 184, 0.28);
  background: #f8fafc;
  color: #64748b;
}

.actions-cell {
  text-align: center;
}

.buttons-container {
  display: inline-flex;
  gap: 10px;
}

.buttons-container button {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #f7f9fc;
  color: #475569;
  cursor: pointer;
}

.empty-state {
  text-align: center;
  color: #64748b;
  font-style: italic;
  padding: 24px !important;
}

@media (max-width: 768px) {
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }

  .create-user-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
